import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { SepTableComponent } from '../../../../../shared/components/sep-table/sep-table.component';
import {
  TableBulkAction,
  TableTargetedAction,
} from '../../../../../shared/components/sep-table/Action';
import { UserCardComponent } from '../../../../../shared/components/user-card/user-card.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { Column } from '../../../../../shared/components/sep-table/Column';
import { EnrollmentNotificationService } from '../../../../../core/services/enrollment-notification.service';
import { CourseService } from '../../services/course.service';
import { CourseDataService } from '../../services/course-data.service';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { FilterCategory } from '../../../../../shared/components/sep-filter/sep-filter.component';
import { CourseEnrolledStudentUIModel } from '../../models/CourseEnrolledStudentUIModel';
import { EnrolledStudentTableBaseComponent } from '../../../base/EnrolledStudentTableBaseComponent';

@Component({
  selector: 'app-learning-plan-enrolled-student-table',
  imports: [SepTableComponent, UserCardComponent, TranslocoDirective],
  templateUrl: './enrolled-student-table.component.html',
  styleUrl: './enrolled-student-table.component.css',
})
export class EnrolledStudentTableComponent
  extends EnrolledStudentTableBaseComponent
  implements AfterViewInit, OnChanges
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly enrollmentNotificationService = inject(
    EnrollmentNotificationService,
  );
  private readonly courseService = inject(CourseService);
  private readonly courseDataService = inject(CourseDataService);
  private readonly snackbarService = inject(SnackbarService);

  @ViewChild('studentCard', { static: true }) studentCard!: TemplateRef<{
    $implicit: string;
  }>;
  @Input({ required: true }) students: CourseEnrolledStudentUIModel[];
  dataSource: CourseEnrolledStudentUIModel[] = [];

  columns: Array<
    Column<CourseEnrolledStudentUIModel, string | { $implicit: string }>
  > = [
    {
      headerName: 'users.table.header.nameAndEmail',
      renderComponent: undefined,
      key: 'username',
    },
    {
      headerName: 'users.table.header.status',
      field: 'courseProgress',
      key: 'courseProgress',
    },
  ];

  bulkActions: Array<TableBulkAction<CourseEnrolledStudentUIModel>> = [
    {
      label: this.getTranslation('courses.unenrollment.bulkUnenrollButtonText'),
      icon: 'group_remove',
      executeAction: (targetedStudents: CourseEnrolledStudentUIModel[]) => {
        this.showUnenrollmentConfirmationDialog(targetedStudents, () => {
          this.unenrollStudents(targetedStudents);
        });
      },
    },
    {
      label: this.getTranslation('courses.reminders.bulkSendReminder'),
      icon: 'send',
      executeAction: (targetedStudents: CourseEnrolledStudentUIModel[]) => {
        this.showSendReminderConfirmationDialog(targetedStudents, () => {
          this.sendReminder(targetedStudents);
        });
      },
    },
  ];

  rowActions: Array<TableTargetedAction<CourseEnrolledStudentUIModel>> = [
    {
      label: this.getTranslation('courses.unenrollment.unenrollButtonText'),
      icon: 'person_remove',
      executeAction: (targetedStudent: CourseEnrolledStudentUIModel) => {
        this.showUnenrollmentConfirmationDialog([targetedStudent], () => {
          this.unenrollStudents([targetedStudent]);
        });
      },
    },
    {
      label: this.getTranslation('courses.reminders.sendReminder'),
      icon: 'send',
      executeAction: (targetedStudent: CourseEnrolledStudentUIModel) => {
        this.showSendReminderConfirmationDialog([targetedStudent], () => {
          this.sendReminder([targetedStudent]);
        });
      },
    },
  ];

  filterCategories: FilterCategory[] | undefined;

  constructor() {
    super();
  }

  ngAfterViewInit(): void {
    this.columns[0].renderComponent = this.studentCard;
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['students'].currentValue !== changes['students'].previousValue
    ) {
      this.dataSource = this.translateProperties(
        changes['students'].currentValue as CourseEnrolledStudentUIModel[],
      );
      this.initializeCourseProgressFilter();
    }
  }

  private unenrollStudents(students: CourseEnrolledStudentUIModel[]) {
    const courseId = this.courseDataService.getCourseId();
    if (!courseId) {
      console.error('No given course Id');
      return;
    }
    this.courseService
      .unenrollStudentsFromCourse(students, courseId)
      .subscribe({
        next: () => {
          this.analyticsService.track('students_unenrolled', {
            courseId: courseId,
            students: students.map(({ id }) => ({ id })),
          });
          this.enrollmentNotificationService.setEnrollmentChanged();
        },
        error: (error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
        },
      });
  }

  translateProperties(
    students: CourseEnrolledStudentUIModel[],
  ): CourseEnrolledStudentUIModel[] {
    return students.map((student) => ({
      ...student,
      courseProgress: this.getTranslation(
        'courses.analytics.courseProgress.' + student.courseProgress,
      ),
    }));
  }

  private initializeCourseProgressFilter(): void {
    const uniqueCourseProgressValues = Array.from(
      new Set(this.students.map((student) => student.courseProgress)),
    );

    this.filterCategories = [
      {
        label: this.getTranslation('users.table.header.status'),
        value: 'courseProgress',
        options: uniqueCourseProgressValues.map((value) => ({
          label: this.getTranslation(
            'courses.analytics.courseProgress.' + value,
          ),
          selected: true,
        })),
      },
    ];
  }

  private sendReminder(students: CourseEnrolledStudentUIModel[]) {
    const courseId = this.courseDataService.getCourseId();
    if (!courseId) {
      console.error('No given course Id');
      return;
    }
    this.courseService
      .sendReminderToStudentsFromCourse(students, courseId)
      .subscribe({
        next: () => {
          this.analyticsService.track('course_reminder_sent', {
            courseId: courseId,
            students: students.map(({ id }) => ({ id })),
          });
        },
        error: (error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
        },
      });
  }

  exportTableContentsToCsv() {
    const courseId = this.courseDataService.getCourseId();
    if (!courseId) {
      return;
    }
    this.courseService.exportStudentInfoToCsv(courseId).subscribe((data) => {
      this.openFile(
        data,
        this.courseDataService.getCourseName() ??
          `CourseExport_${new Date().toISOString().slice(0, 10)}`,
      );
    });
  }
}
